import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import { MemberDetail, StageGroup, usageService } from '../services/UsageService';
import {
  BORDER, DEEP, MemberAvatar, lastActiveLabel, memberName, memberRole, monthLabel, num,
} from '../components/features/usage/memberUsageUtils';

const STAGE_COLORS: Record<StageGroup, string> = {
  quoted: DEEP,
  in_progress: '#4f46e5',
  needs_answer: '#e8913a',
  closed: '#8a857b',
};
const STAGE_PILL: Record<StageGroup, string> = {
  quoted: 'bg-[#eceafd] text-[#2a1ca8]',
  in_progress: 'bg-[#eef2ff] text-[#3730a3]',
  needs_answer: 'bg-[#fdf0e1] text-[#8a4b0c]',
  closed: 'bg-[#f1efea] text-[#4a463f]',
};

/** One advisor's month: totals, daily activity, order stages, recent orders. */
const UsageMemberPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const { memberKey = '' } = useParams<{ memberKey: string }>();
  const [params, setParams] = useSearchParams();
  const month = params.get('month') || undefined;
  const [metric, setMetric] = useState<'quotes' | 'searches'>('quotes');
  const [data, setData] = useState<MemberDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    usageService
      .getMemberDetail(memberKey, month)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404
          ? t('usage.members.notFound', { defaultValue: 'This advisor has no usage in your organization.' })
          : t('usage.members.loadError', { defaultValue: 'Could not load advisor usage.' }));
      });
    return () => { cancelled = true; };
  }, [memberKey, month, t]);

  const stageLabels: Record<StageGroup, string> = {
    quoted: t('usage.members.stageQuoted', { defaultValue: 'Quoted / sent' }),
    in_progress: t('usage.members.stageInProgress', { defaultValue: 'In progress' }),
    needs_answer: t('usage.members.stageNeedsAnswer', { defaultValue: 'Needs an answer' }),
    closed: t('usage.members.stageClosed', { defaultValue: 'Closed without quote' }),
  };

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.daily[metric].map((v, i) => ({ day: i + 1, value: v }));
  }, [data, metric]);

  const shownMonth = data?.month || month;
  const title = t('usage.title', { defaultValue: 'Usage & Plan' });
  const name = data ? memberName(data.member.key, data.member.name, t) : '';

  return (
    <AppLayout pageTitle={title}>
      <MobileHeader title={title} />
      <div className="flex-1 overflow-auto bg-main-bg min-h-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8">
          <Link
            to={`/usage${shownMonth ? `?month=${shownMonth}` : ''}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium min-h-[44px]"
            style={{ color: DEEP }}
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            {t('usage.members.allAdvisorsBack', { defaultValue: 'All advisors' })}
          </Link>

          {error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>}

          {data && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mt-2">
                <div className="flex items-center gap-4">
                  <MemberAvatar name={data.member.name} kind={data.member.kind} size={56} />
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900">{name}</h1>
                    <p className="text-sm text-gray-500 mt-1">
                      {[
                        memberRole(data.member.role, t),
                        data.member.email,
                        t('usage.members.activeDays', {
                          defaultValue: 'active {{count}} of {{days}} days',
                          count: data.totals.active_days,
                          days: data.daily.quotes.length,
                        }),
                        `${t('usage.members.lastActive', { defaultValue: 'Last active' }).toLowerCase()}: ${lastActiveLabel(data.totals.last_active, locale, t)}`,
                      ].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
                <input
                  type="month"
                  aria-label={t('usage.billingMonth', { defaultValue: 'Billing month' })}
                  value={data.month}
                  onChange={(e) => e.target.value && setParams({ month: e.target.value })}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 min-h-[40px]"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
                {[
                  [t('usage.metrics.quotes', { defaultValue: 'Quotes' }), num(data.totals.quotes)],
                  [t('usage.metrics.part_queries', { defaultValue: 'Part searches' }), num(data.totals.searches)],
                  [t('usage.metrics.images', { defaultValue: 'Images' }), num(data.totals.images)],
                  [t('usage.metrics.pdfs', { defaultValue: 'PDFs' }), num(data.totals.pdfs)],
                  [t('usage.members.partsFound', { defaultValue: 'Parts found' }),
                    data.totals.parts_found_pct == null ? '—' : `${data.totals.parts_found_pct}%`],
                ].map(([label, value]) => (
                  <div key={label} className="bg-white rounded-xl p-4" style={{ border: `1px solid ${BORDER}` }}>
                    <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
                    <div className="text-2xl font-semibold text-gray-900 mt-1.5">{value}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                <div className="lg:col-span-2 bg-white rounded-2xl p-5" style={{ border: `1px solid ${BORDER}` }}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-base font-semibold text-gray-900">
                      {t('usage.dailyActivity', { defaultValue: 'Daily activity' })} · {monthLabel(data.month, locale)}
                    </h2>
                    <div role="group" aria-label={t('usage.members.metric', { defaultValue: 'Metric' })} className="flex gap-1 bg-[#f4f2ee] p-1 rounded-lg">
                      {(['quotes', 'searches'] as const).map((m) => (
                        <button
                          key={m}
                          type="button"
                          aria-pressed={metric === m}
                          onClick={() => setMetric(m)}
                          className={`text-[13px] px-3 py-1 min-h-[32px] rounded-md border ${metric === m ? 'bg-white border-gray-200 text-gray-900 font-semibold' : 'border-transparent text-gray-600 font-medium'}`}
                        >
                          {m === 'quotes'
                            ? t('usage.metrics.quotes', { defaultValue: 'Quotes' })
                            : t('usage.metrics.part_queries', { defaultValue: 'Part searches' })}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="h-48 mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="#f1eee8" />
                        <XAxis dataKey="day" tickLine={false} axisLine={false} interval={4} tick={{ fontSize: 11, fill: '#6b665c' }} />
                        <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#6b665c' }} />
                        <Tooltip cursor={{ fill: '#faf9f6' }} labelFormatter={(d) => `${monthLabel(data.month, locale, true)} ${d}`} />
                        <Bar
                          dataKey="value"
                          name={metric === 'quotes'
                            ? t('usage.metrics.quotes', { defaultValue: 'Quotes' })
                            : t('usage.metrics.part_queries', { defaultValue: 'Part searches' })}
                          fill="#4f46e5"
                          radius={[3, 3, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-5 flex flex-col gap-3.5" style={{ border: `1px solid ${BORDER}` }}>
                  <h2 className="text-base font-semibold text-gray-900">
                    {t('usage.members.whereOrders', { defaultValue: 'Where their orders are' })}
                  </h2>
                  {(Object.keys(stageLabels) as StageGroup[]).map((g) => (
                    <div key={g} className="flex flex-col gap-1.5">
                      <div className="flex justify-between text-sm">
                        <span>{stageLabels[g]}</span>
                        <span className="font-semibold tabular-nums">{num(data.stages[g])}</span>
                      </div>
                      <div className="h-2 bg-[#eeebe4] rounded overflow-hidden">
                        <div
                          className="h-full rounded"
                          style={{ background: STAGE_COLORS[g], width: `${data.totals.orders ? Math.round((data.stages[g] / data.totals.orders) * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-[13px] text-gray-500 mt-1">
                    {t('usage.members.ordersCreated', { defaultValue: '{{count}} orders created', count: data.totals.orders })}
                  </p>
                </div>
              </div>

              <section aria-labelledby="member-orders-h" className="relative bg-white rounded-2xl overflow-hidden mt-4" style={{ border: `1px solid ${BORDER}` }}>
                <div className="px-5 py-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <h2 id="member-orders-h" className="text-base font-semibold text-gray-900">
                    {t('usage.members.ordersThisMonth', { defaultValue: 'Orders created this month' })}
                  </h2>
                </div>
                {data.orders.length === 0 ? (
                  <p className="px-5 py-6 text-sm text-gray-500">
                    {t('usage.members.noOrders', { defaultValue: 'No orders this month.' })}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-sm">
                      <thead>
                        <tr className="bg-[#faf9f6] text-xs uppercase tracking-wider text-gray-500">
                          <th scope="col" className="text-left font-medium px-5 py-3">{t('usage.members.order', { defaultValue: 'Order' })}</th>
                          <th scope="col" className="text-left font-medium px-3 py-3">{t('usage.members.vehicle', { defaultValue: 'Vehicle' })}</th>
                          <th scope="col" className="text-left font-medium px-3 py-3">{t('usage.members.stage', { defaultValue: 'Stage' })}</th>
                          <th scope="col" className="text-right font-medium px-3 py-3">{t('usage.metrics.part_queries', { defaultValue: 'Part searches' })}</th>
                          <th scope="col" className="text-right font-medium px-3 py-3">{t('usage.members.found', { defaultValue: 'Found' })}</th>
                          <th scope="col" className="text-right font-medium px-3 py-3">PDF</th>
                          <th scope="col" className="text-left font-medium px-3 py-3">{t('usage.members.created', { defaultValue: 'Created' })}</th>
                          <th scope="col" className="px-5 py-3"><span className="sr-only">{t('usage.members.open', { defaultValue: 'Open' })}</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.orders.map((o) => (
                          <tr key={o.id} className="border-b hover:bg-[#faf9f6]" style={{ borderColor: '#f1eee8' }}>
                            <td className="px-5 py-2.5 font-mono text-[13px]">{o.external_order_id || `#${o.id}`}</td>
                            <td className="px-3 py-2.5">{o.vehicle || o.vin || '—'}</td>
                            <td className="px-3 py-2.5">
                              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${STAGE_PILL[o.stage_group]}`}>
                                {stageLabels[o.stage_group]}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{num(o.searches)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">
                              {o.parts_requested ? `${o.parts_found} / ${o.parts_requested}` : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right">{o.pdf ? t('usage.members.yes', { defaultValue: 'Yes' }) : '—'}</td>
                            <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                              {new Date(o.created_at).toLocaleString(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-5 py-2.5 text-right">
                              {o.session_id && (
                                <Link
                                  to={`/chat/${o.session_id}?order_id=${o.id}`}
                                  className="font-medium inline-flex items-center min-h-[36px]"
                                  style={{ color: DEEP }}
                                  aria-label={t('usage.members.openOrder', { defaultValue: 'Open order {{id}}', id: o.external_order_id || o.id })}
                                >
                                  {t('usage.members.open', { defaultValue: 'Open' })}
                                </Link>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default UsageMemberPage;
